import { createSlice } from '@reduxjs/toolkit';
import {
  fetchItems, fetchItem, updateItem,
} from '../items/itemsSlice';
import { fetchAuthors, fetchAuthorById, updateAuthor, replaceAuthor } from '../authors/authorsSlice';
import { fetchLists, fetchList } from '../lists/listsSlice';
import { fetchSites, fetchSiteById, updateSite } from '../sites/sitesSlice';
import { fetchTags } from '../tags/tagsSlice';
import {
  populateItemTags, populateItemSites, populateItemAuthors, populateItemLists, populateItemsResponse,
} from '../../../common/ItemTools';

const DEFAULT_PAGE_SIZE = 8;
const PAGE_SIZE_LISTS = 12;
const PAGE_SIZE_STORAGE_KEY = 'cl.pageSize';

// Load the user's manual items-per-page override, persisted across sessions.
function loadPageSize() {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (!stored) return null;
    const value = parseInt(stored, 10);
    return Number.isNaN(value) ? null : value;
  } catch (error) {
    return null;
  }
}

// Reflect an author returned by an update/replace into the current context,
// both in the authors list and as the focused author when present.
function applyAuthorUpdate(state, action) {
  const updatedAuthor = action.payload;

  if (state.context?.authors && Array.isArray(state.context.authors)) {
    const authorIndex = state.context.authors.findIndex((author) => author.id === updatedAuthor.id);
    if (authorIndex !== -1) {
      state.context.authors[authorIndex] = updatedAuthor;
    }
  }

  if (state.context?.author && state.context.author.id === updatedAuthor.id) {
    state.context.author = updatedAuthor;
  }
}

/**
 * Context slice manages the current view/page state
 * This includes what type of content is being displayed (items, lists, authors, etc.)
 * and any associated metadata (filters, pagination, etc.)
 */
const initialState = {
  context: null,
  status: 'idle',
  error: null,
  selectionMode: false,
  selectedIds: [],
  pageSize: loadPageSize(),
  zenMode: false,
};

const contextSlice = createSlice({
  name: 'context',
  initialState,
  reducers: {
    enterSelectionMode: (state, action) => {
      state.selectionMode = true;
      state.selectedIds = action.payload || [];
    },
    exitSelectionMode: (state) => {
      state.selectionMode = false;
      state.selectedIds = [];
    },
    recoveringLoad: (state) => {
      state.status = 'loading';
    },
    toggleSelection: (state, action) => {
      const id = action.payload;
      const index = state.selectedIds.indexOf(id);

      if (index >= 0) {
        state.selectedIds.splice(index, 1);
      } else {
        state.selectedIds.push(id);
      }

      if (state.selectedIds.length === 0) {
        state.selectionMode = false;
      }
    },
    setSelectedIds: (state, action) => {
      state.selectedIds = action.payload;
      state.selectionMode = state.selectedIds.length > 0;
    },
    setZenMode: (state, action) => {
      state.zenMode = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      try {
        if (action.payload == null) {
          localStorage.removeItem(PAGE_SIZE_STORAGE_KEY);
        } else {
          localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(action.payload));
        }
      } catch (error) {
        // Ignore storage failures
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        const {
          items, itemsCount, tags, sites, authors, lists, stats,
        } = action.payload;
        const {
          pageNumber = 1, filter, tagId, view, sortby, source,
        } = action.meta.arg.params || {};

        if (tags) {
          populateItemTags(items, tags.privateData, tags.publicData);
        }
        if (sites) {
          populateItemSites(items, sites);
        }
        if (authors) {
          populateItemAuthors(items, authors);
        }
        if (lists) {
          populateItemLists(items, lists);
        }

        const contextStats = stats || { totalItems: itemsCount };

        state.context = {
          type: 'items',
          namespace: action.meta.arg.namespace,
          items,
          itemsCount,
          pagesCount: Math.ceil(itemsCount / (action.meta.arg.params?.limit || DEFAULT_PAGE_SIZE)),
          pageNumber,
          ...(filter && { filter }),
          ...(tagId && { tagId }),
          ...(view && { view }),
          ...(sortby && { sortby }),
          ...(source && { source }),
          ...(tags && { tags }),
          ...(sites && { sites }),
          ...(authors && { authors }),
          ...(lists && { lists }),
          ...(contextStats && { stats: contextStats }),
        };
        state.status = 'succeeded';
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchItem.fulfilled, (state, action) => {
        const {
          item, authors, tags, sites, listInfos,
        } = action.payload;

        if (item) {
          populateItemsResponse([item], action.payload);
        }

        state.context = {
          type: 'item',
          namespace: action.meta.arg.namespace,
          item,
          ...(authors && { authors }),
          ...(tags && { tags }),
          ...(sites && { sites }),
          ...(listInfos && { listInfos }),
        };
        state.status = 'succeeded';
      })
      .addCase(fetchItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchAuthors.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAuthors.fulfilled, (state, action) => {
        const {
          authors, count,
        } = action.payload;
        const {
          pageNumber = 1, filter, view, sortby, source,
        } = action.meta.arg.params || {};

        state.context = {
          type: 'authors',
          namespace: action.meta.arg.namespace,
          authors,
          authorCount: count,
          pagesCount: Math.ceil(count / (action.meta.arg.params?.limit || DEFAULT_PAGE_SIZE)),
          pageNumber,
          ...(filter && { filter }),
          ...(view && { view }),
          ...(sortby && { sortby }),
          ...(source && { source }),
        };
        state.status = 'succeeded';
      })
      .addCase(fetchAuthors.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchLists.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchLists.fulfilled, (state, action) => {
        const {
          lists, count,
        } = action.payload;
        const {
          pageNumber = 1, filter, view, sortby, source,
        } = action.meta.arg.params || {};

        state.context = {
          type: 'lists',
          namespace: action.meta.arg.namespace,
          lists,
          listCount: count,
          pagesCount: Math.ceil(count / PAGE_SIZE_LISTS),
          pageNumber,
          ...(filter && { filter }),
          ...(view && { view }),
          ...(sortby && { sortby }),
          ...(source && { source }),
        };
        state.status = 'succeeded';
      })
      .addCase(fetchLists.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchList.fulfilled, (state, action) => {
        const { stats, ...listInfo } = action.payload;
        state.context = {
          type: 'list',
          namespace: action.meta.arg.namespace,
          listInfo,
          listId: action.meta.arg.listId,
          ...(stats && { stats }),
        };
        state.status = 'succeeded';
      })
      .addCase(fetchList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchSites.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSites.fulfilled, (state, action) => {
        const {
          sites, count,
        } = action.payload;
        const {
          pageNumber = 1, filter, view, sortby, source,
        } = action.meta.arg.params || {};

        state.context = {
          type: 'sites',
          namespace: action.meta.arg.namespace,
          sites,
          siteCount: count,
          pagesCount: Math.ceil(count / (action.meta.arg.params?.limit || DEFAULT_PAGE_SIZE)),
          pageNumber,
          ...(filter && { filter }),
          ...(view && { view }),
          ...(sortby && { sortby }),
          ...(source && { source }),
        };
        state.status = 'succeeded';
      })
      .addCase(fetchSites.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchSiteById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSiteById.fulfilled, (state, action) => {
        state.context = {
          type: 'site',
          namespace: action.meta.arg.namespace,
          site: action.payload,
        };
        state.status = 'succeeded';
      })
      .addCase(fetchSiteById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchAuthorById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAuthorById.fulfilled, (state, action) => {
        state.context = {
          type: 'author',
          namespace: action.meta.arg.namespace,
          author: action.payload,
        };
        state.status = 'succeeded';
      })
      .addCase(fetchAuthorById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchTags.fulfilled, (state, action) => {
        const { type, namespace } = action.meta.arg;
        if (type) {
          state.context = {
            type,
            namespace,
            tags: action.payload,
          };
          state.status = 'succeeded';
        }
      })
      .addCase(updateItem.fulfilled, (state, action) => {
        const response = action.payload;
        const { item: updatedItem, tags, authors, sites } = response;

        if (tags) {
          populateItemTags([updatedItem], tags.privateData, tags.publicData);
        }
        if (sites) {
          populateItemSites([updatedItem], sites);
        }
        if (authors) {
          populateItemAuthors([updatedItem], authors);
        }

        if (state.context?.items && Array.isArray(state.context.items)) {
          const itemIndex = state.context.items.findIndex((item) => item.id === updatedItem.id);
          if (itemIndex !== -1) {
            state.context.items[itemIndex] = updatedItem;
          }
        }

        if (state.context?.item && state.context.item.id === updatedItem.id) {
          state.context.item = updatedItem;
        }
      })
      .addCase(updateAuthor.fulfilled, applyAuthorUpdate)
      .addCase(replaceAuthor.fulfilled, applyAuthorUpdate)
      .addCase(updateSite.fulfilled, (state, action) => {
        const updatedSite = action.payload;

        if (state.context?.sites && Array.isArray(state.context.sites)) {
          const siteIndex = state.context.sites.findIndex((site) => site.id === updatedSite.id);
          if (siteIndex !== -1) {
            state.context.sites[siteIndex] = updatedSite;
          }
        }

        if (state.context?.site && state.context.site.id === updatedSite.id) {
          state.context.site = updatedSite;
        }
      })
      // Auto-exit selection mode when navigating
      .addMatcher(
        (action) => action.type.endsWith('/pending') &&
                   (action.type.startsWith('items/fetch')),
        (state) => {
          state.selectionMode = false;
          state.selectedIds = [];
        }
      );
  },
});

export const {
  enterSelectionMode, exitSelectionMode, recoveringLoad, setPageSize, setSelectedIds, setZenMode, toggleSelection,
} = contextSlice.actions;

export default contextSlice.reducer;

// Selectors
export const selectContext = (state) => state.context.context;
export const selectContextStatus = (state) => state.context.status;
export const selectContextError = (state) => state.context.error;
export const selectContextNamespace = (state) => state.context.context?.namespace;
export const selectSelectionMode = (state) => state.context.selectionMode;
export const selectSelectedIds = (state) => state.context.selectedIds;
export const selectSelectedCount = (state) => state.context.selectedIds?.length || 0;
export const selectPageSize = (state) => state.context.pageSize;
export const selectZenMode = (state) => state.context.zenMode;

/**
 * Get namespace path for routing
 */
export function getNamespacePath(namespace) {
  switch (namespace) {
    case 'public':
      return 'explore';
    case 'pending':
      return 'moderate';
    default:
      return 'learn';
  }
}
